import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { VehicleActivity, VehicleBlock, StateOfChargeMeasurementPoint, VehicleStateOfChargeData } from '../../shared/models/vehicle';
import { Observable, of, catchError, map } from 'rxjs';
import { handleError } from './service.helpers';

import * as _ from 'lodash';
import { ConfigService } from 'src/config';
import { VehicleDepartureHistory } from '../../shared/models/vehicle/vehicle-departure-history';

@Injectable({
    providedIn: 'root'
})
export class VehicleInfoService {
    private _http = inject(HttpClient);
    private _config = inject(ConfigService);


    constructor() { }

    // vehicleActivityId, eller garageCode, routeName and tripNumber
    public getVehicleActivityById(vehicleActivityId: string): Observable<VehicleActivity> {
        const url = `${this._config.mapApiBaseUrl}/v1/vehicleActivities/${vehicleActivityId}`;
        return this._http.get<VehicleActivity>(url)
            .pipe(
                map(item => new VehicleActivity(item)),
                catchError(error => {
                    handleError(error);
                    return of(undefined);
                })
            );
    }

    public getVehicleActivity(inventoryNumber: string, routeName: string, tripNumber: string): Observable<VehicleActivity> {
        // eslint-disable-next-line max-len
        const url = `${this._config.mapApiBaseUrl}/v1/vehicleActivities?inventoryNumber=${inventoryNumber}&routeName=${routeName}&tripNumber=${tripNumber}`;
        return this._http.get<VehicleActivity>(url)
            .pipe(
                map(item => new VehicleActivity(item)),
                catchError(error => {
                    handleError(error);
                    return of(undefined);
                })
            );
    }

    public getVehicleActivities(inventoryNumber: string, trafficDate?: string): Observable<VehicleBlock> {
        let url = `${this._config.mapApiBaseUrl}/v1/vehicles/${inventoryNumber}/activities`;

        if (trafficDate) {
            url += `?trafficDate=${trafficDate}`;
        }

        return this._http.get<VehicleBlock>(url)
            .pipe(
                map(item => new VehicleBlock(item)),
                catchError(error => {
                    handleError(error);
                    return of(undefined);
                })
            );
    }

    public getVehicleStateOfChargeData(inventoryNumber: string): Observable<VehicleStateOfChargeData> {
        const url = `${this._config.cmApiBaseUrl}/api/vehicles/${inventoryNumber}/socData`;

        return this._http.get<any>(url)
            .pipe(
                map((socData: any) => new VehicleStateOfChargeData({
                    vehicleInventoryNumber: socData?.vehicleInventoryNumber,
                    history: this._mapStateOfChargeMeasurementPoints(socData?.history),
                    prognosis: this._mapStateOfChargeMeasurementPoints(socData?.prognosis),
                })),
                catchError(error => {
                    handleError(error);
                    return of(undefined);
                }));
    }

    public getVehicleDepartureHistory(inventoryNumber: string): Observable<VehicleDepartureHistory> {
        const url = `${this._config.mapApiBaseUrl}/v1/vehicles/${inventoryNumber}/departureHistory`;

        return this._http.get<VehicleDepartureHistory>(url).pipe(
            map(item => new VehicleDepartureHistory(item)),
            catchError(error => {
                handleError(error);
                return of(undefined);
            })
        );
    }

    public getRoutePaths(garageCode: string, routeName: string, tripNumbers: string[]): Observable<any> {
        const tripNumbersAsString = _.toString(tripNumbers);
        // eslint-disable-next-line max-len
        const url = `${this._config.mapApiBaseUrl}/v1/routes/${routeName}?garageCode=${garageCode}&tripNumbers=${tripNumbersAsString}`;
        return this._http.get<any>(url)
            .pipe(
                map(item => item.geoJsons),
                catchError(error => {
                    handleError(error);
                    return of(undefined);
                })
            );
    }

    private _mapStateOfChargeMeasurementPoints(socs: any[]): StateOfChargeMeasurementPoint[] {
        return _.sortBy(
            (socs ?? []).map(soc => new StateOfChargeMeasurementPoint({
                sensorValue: soc.value,
                measuredAt: soc.time,
            })),
            soc => new Date(soc.measuredAt).getTime()
        );
    }
}
